"use client";

import { useState, useEffect, useCallback } from "react";

// =============================================
// Constants
// =============================================

const SAVE_LOGIN_KEY = "vastipost:save_login";
const REMEMBERED_EMAIL_KEY = "vastipost:remembered_email";

// =============================================
// Types
// =============================================

export interface SaveLoginState {
  /** Whether save login is enabled */
  isEnabled: boolean;
  /** The remembered email (if any) */
  rememberedEmail: string | null;
  /** Whether the state has been loaded from storage */
  isLoaded: boolean;
}

export interface UseSaveLoginReturn extends SaveLoginState {
  /** Enable save login and optionally store email */
  enableSaveLogin: (email?: string) => void;
  /** Disable save login and clear stored data */
  disableSaveLogin: () => Promise<void>;
  /** Update the remembered email */
  setRememberedEmail: (email: string) => void;
  /** Clear the remembered email without disabling save login */
  clearRememberedEmail: () => void;
}

// =============================================
// Hook
// =============================================

/**
 * Hook to manage save login preference and remembered email.
 * 
 * When enabled:
 * - Stores preference in localStorage
 * - Stores email for autofill
 * - Integrates with WebAuthn for biometric protection
 * 
 * When disabled:
 * - Clears all stored login data
 * - Removes device from remembered accounts
 */
export function useSaveLogin(): UseSaveLoginReturn {
  const [isEnabled, setIsEnabled] = useState(false);
  const [rememberedEmail, setRememberedEmailState] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(SAVE_LOGIN_KEY);
      const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
      
      setIsEnabled(savedPreference === "true");
      setRememberedEmailState(savedEmail);
      setIsLoaded(true);
    } catch (error) {
      console.error("[useSaveLogin] Failed to load from localStorage:", error);
      setIsLoaded(true);
    }
  }, []);

  // Enable save login
  const enableSaveLogin = useCallback((email?: string) => {
    try {
      localStorage.setItem(SAVE_LOGIN_KEY, "true");
      setIsEnabled(true);
      
      if (email) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
        setRememberedEmailState(email);
      }
    } catch (error) {
      console.error("[useSaveLogin] Failed to save to localStorage:", error);
    }
  }, []);

  // Disable save login and clear all data
  const disableSaveLogin = useCallback(async () => {
    try {
      // Clear localStorage items
      localStorage.removeItem(SAVE_LOGIN_KEY);
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      localStorage.removeItem("vastipost:active_brand_id");
      
      // Clear session storage
      sessionStorage.removeItem("vastipost_session_state");
      
      // Update state
      setIsEnabled(false);
      setRememberedEmailState(null);
      
      // Call API to remove remembered accounts for this device
      try {
        const response = await fetch("/api/auth/remembered-accounts", {
          method: "DELETE",
        });
        
        if (!response.ok) {
          console.warn("[useSaveLogin] Failed to clear remembered accounts from server");
        }
      } catch (apiError) {
        console.error("[useSaveLogin] API call failed:", apiError);
      }
    } catch (error) {
      console.error("[useSaveLogin] Failed to disable save login:", error);
    }
  }, []);

  // Update remembered email
  const setRememberedEmail = useCallback((email: string) => {
    try {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      setRememberedEmailState(email);
    } catch (error) {
      console.error("[useSaveLogin] Failed to save email:", error);
    }
  }, []);

  // Clear remembered email only
  const clearRememberedEmail = useCallback(() => {
    try {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      setRememberedEmailState(null);
    } catch (error) {
      console.error("[useSaveLogin] Failed to clear email:", error);
    }
  }, []);

  return {
    isEnabled,
    rememberedEmail,
    isLoaded,
    enableSaveLogin,
    disableSaveLogin,
    setRememberedEmail,
    clearRememberedEmail,
  };
}

export default useSaveLogin;
